/* eslint-disable no-console */
import React, { useContext, useEffect, useState } from "react";

import { createGenerateClassName, StylesProvider } from "@mui/styles";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import * as yup from "yup";

import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { PRIVATE_ROUTES } from "../../../../../../../constants";
import { useAppState } from "../../../../../../../context/StateContext";
import useActionSyncUser from "../../../../../../../hooks/useSyncUser";
import { MemberStaff } from "../../../../../../../models/members/member.staff";
import { MemberUser } from "../../../../../../../models/members/member.user";
import MembersService from "../../../../../../../services/members.service";
import { MembersType, Status } from "../../../../../../../util/enums";
import { currencyToNumber } from "../../../../../../../util/prices";
import { MemberKeys } from "../../../../../../../util/queries";
import { CreateComponent } from "./components/CreateComponent";
import { EditComponent } from "./components/EditComponent";
import { Header } from "./components/Header";
import { MemberContext, MemberProvider } from "./context";
import { Wrapper } from "./styles";
import Unauthorized from "Components/Unauthorized";
import differenceInDays from "date-fns/differenceInDays";

const generateClassName = createGenerateClassName({
  productionPrefix: "mnt-",
  seed: "maintenance-page",
});

const MainentanceContent: React.FC = () => {
  const { setHasUserChanged, currentUser } = useAppState();
  const { mutate: syncUser } = useActionSyncUser();

  const { t: translate } = useTranslation();
  const { memberId } = useParams<{ memberId?: string }>();
  const history = useHistory<{ tab?: string; tabPeople?: string; isView?: boolean }>();
  const { member, memberHasShifted, setMember, setMemberHasShifted, refetchMembers } =
    useContext(MemberContext);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [notFound, setNotFound] = useState<boolean>(false);

  const isPendingAcceptance =
    member?.status?.toLowerCase() === Status.PENDING_ACCEPTANCE.toLowerCase();

  useEffect(() => {
    if (!memberId || !isPendingAcceptance || history.location.state?.isView) return;

    history.replace({
      pathname: history.location.pathname,
      search: history.location.search,
      state: { ...(history.location.state || {}), isView: true },
    });
  }, [memberId, isPendingAcceptance, history]);

  const { isFetching } = useQuery({
    queryKey: MemberKeys.getById(Number(memberId)),
    queryFn: async () => {
      const response = await MembersService.getByFilter({
        id: Number(memberId),
        companyId: Number(currentUser?.companyId),
        include: JSON.stringify([
          {
            association: "picture",
            attributes: ["id", "mediaLink", "name"],
          },
          {
            association: "teams",
            attributes: ["id", "name"],
          },
          {
            association: "subcontractor",
          },
          {
            association: "branches",
            attributes: ["id", "name"],
          },
          {
            association: "citizenship",
            attributes: ["id", "name"],
          },
          {
            association: "country",
            attributes: ["id", "name"],
          },
          {
            association: "timezone",
          },
          {
            association: "location",
          },
          {
            association: "staff",
            attributes: [
              "id",
              "email",
              "emergencyName",
              "emergencyPhone",
              "residencyStatus",
              "emergencyRelationship",
            ],
          },
          {
            association: "user",
            attributes: ["id", "username"],
          },
          {
            association: "userCompany",
            include: [
              {
                association: "role",
              },
              {
                association: "applications",
              },
              {
                association: "manager",
              },
            ],
          },
        ]),
      });
      const member = response?.data;

      if (member) {
        const isStaff =
          member.typeOfMember?.toLowerCase() === MembersType.STAFF.toLowerCase();

        let groupId: number | undefined;
        if (!isStaff && member.userId && member.companyId) {
          try {
            const groupResponse = await MembersService.getGroupByUser(
              Number(member.userId),
              Number(member.companyId)
            );
            groupId = groupResponse?.data?.[0]?.groupId;
          } catch {
            groupId = undefined;
          }
        }

        if (isStaff) {
          const currentMember: MemberStaff = {
            ...member,
            emergencyEmail: member.staff?.email,
            emergencyName: member.staff?.emergencyName,
            emergencyPhone: member.staff?.emergencyPhone,
            residencyStatus: member.staff?.residencyStatus,
            emergencyRelationship: member.staff?.emergencyRelationship,
          } as MemberStaff;

          setMember(currentMember);
        } else {
          const currentMember: MemberUser = {
            ...member,
            typeOfUser: member.userCompany?.typeUser || "",
            username: member.user?.username || "",
            code: member.userCompany?.code,
            applications: member.userCompany?.applications,
            role: member.userCompany?.role,
            roleId: Number(member.userCompany?.roleId),
            manager: member.userCompany?.manager,
            leader: member.userCompany?.leader || false,
            groupId,
          } as MemberUser;

          setMember(currentMember);
        }
        setNotFound(false);
      } else {
        setNotFound(true);
      }

      return member;
    },
    enabled: !!memberId,
    refetchOnWindowFocus: false,
  });

  const { mutate: updateMember, isLoading: isUpdating } = useMutation({
    mutationKey: MemberKeys.update(),
    mutationFn: async ({ id, member }: { id: number; member: MemberStaff | MemberUser }) =>
      MembersService.updateMember(id, member),
    onSuccess: () => {
      NotificationToast.success(translate("operationPerformedSuccessfully"));
      const state = history.location.state as any;
      history.push({
        pathname: PRIVATE_ROUTES.SETTINGS,
        state: state ?? {},
      });
      refetchMembers();

      if (currentUser?.id === member?.userId) {
        setHasUserChanged(true);
        syncUser();
      }
    },
  });

  const { mutate: createMember, isLoading: isCreating } = useMutation({
    mutationKey: MemberKeys.create(),
    mutationFn: async ({ member }: { member: MemberStaff | MemberUser }) => {
      const response = await MembersService.createMember(member);
      return response;
    },
    onSuccess: () => {
      NotificationToast.success(translate("operationPerformedSuccessfully"));
      const state = history.location.state as any;
      history.push({
        pathname: PRIVATE_ROUTES.SETTINGS,
        state: state ?? {},
      });
      refetchMembers();
    },
  });

  const schema = yup.object().shape({
    belongsToASubcontractor: yup.boolean(),
    subcontractorId: yup.number().nullable(),
    typeOfMember: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    firstName: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    lastName: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    status: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    ratePerHour: yup.string().nullable(),
    email: yup.string().when("typeOfMember", {
      is: (typeOfMember: string) => typeOfMember?.toLowerCase() === MembersType.STAFF.toLowerCase(),
      then: yup.string().nullable(),
      otherwise: yup
        .string()
        .email()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired")),
    }),
    mobile: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    phone: yup.string().nullable(),
    residencyStatus: yup.string().when("typeOfMember", {
      is: (typeOfMember: string) => typeOfMember?.toLowerCase() === MembersType.STAFF.toLowerCase(),
      then: yup
        .string()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired")),
      otherwise: yup.string().nullable(),
    }),
    groupId: yup.number().when("typeOfMember", {
      is: (typeOfMember: string) => typeOfMember?.toLowerCase() === MembersType.USER.toLowerCase(),
      then: yup
        .number()
        .min(1)
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired")),
      otherwise: yup.number().nullable(),
    }),
    typeOfUser: yup.string().when("typeOfMember", {
      is: (typeOfMember: string) => typeOfMember?.toLowerCase() === MembersType.USER.toLowerCase(),
      then: yup
        .string()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired")),
      otherwise: yup.string().nullable(),
    }),
    username: yup.string().when("typeOfMember", {
      is: (typeOfMember: string) => typeOfMember?.toLowerCase() === MembersType.USER.toLowerCase(),
      then: yup
        .string()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired")),
      otherwise: yup.string().nullable(),
    }),
    code: yup.string().when("typeOfMember", {
      is: (typeOfMember: string) => typeOfMember?.toLowerCase() === MembersType.STAFF.toLowerCase(),
      then: yup.string().nullable(),
      otherwise: yup
        .string()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired")),
    }),
    branches: yup
      .array()
      .required(translate("thisFieldIsRequired"))
      .min(1, translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
  });

  const onValidate = () => {
    try {
      schema.validateSync(member, { abortEarly: false });
      setErrors([]);

      return true;
    } catch (error) {
      console.log(error);
      if (error instanceof yup.ValidationError) {
        console.log(error?.inner);
        setErrors(error?.inner);
      }

      return false;
    }
  };

  const validateStartEmploymentDate = () => {
    if (!member?.startEmploymentDate && !member?.endEmploymentDate) {
      return Status.ACTIVE;
    }

    if (
      member?.startEmploymentDate &&
      differenceInDays(new Date(member.startEmploymentDate), new Date()) > 0
    ) {
      return Status.INACTIVE;
    }

    return Status.ACTIVE;
  };

  const onSubmit = () => {
    if (isPendingAcceptance) return false;

    const isValid = onValidate();

    const memberStatus = validateStartEmploymentDate();

    if (isValid) {
      if (member?.id) {
        updateMember({
          id: member.id,
          member: {
            ...member,
            ratePerHour: member?.ratePerHour
              ? currencyToNumber(String(member?.ratePerHour))
              : undefined,
          },
        });
      } else {
        createMember({
          member: {
            ...member,
            status: memberStatus,
            ratePerHour: member?.ratePerHour
              ? currencyToNumber(String(member?.ratePerHour))
              : undefined,
          },
        });
      }

      return true;
    }

    return false;
  };

  useEffect(() => {
    if (memberHasShifted) {
      onValidate();
      setMemberHasShifted?.(false);
    }
  }, [memberHasShifted]);

  if (notFound) return <Unauthorized />;

  return (
    <>
      <Header />
      {memberId ? (
        <EditComponent
          isLoading={isFetching}
          isUpdating={isUpdating}
          onSubmit={onSubmit}
          errors={errors}
        />
      ) : (
        <CreateComponent errors={errors} isCreating={isCreating} onSubmit={onSubmit} />
      )}
    </>
  );
};

export default function Maintenance() {
  return (
    <StylesProvider generateClassName={generateClassName}>
      <Wrapper>
        <MemberProvider>
          <MainentanceContent />
        </MemberProvider>
      </Wrapper>
    </StylesProvider>
  );
}
